import { Dispatch, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import JobParamsQuery from '@graphql/queries/JobParamsQuery';
import RefFlavorsByReportId, {
  RefFlavorsByReportIdResponse,
} from '@graphql/queries/RefFlavorsByReportId';
import ReportFlavorSharesQuery, {
  ReportFlavorSharesResponse,
} from '@graphql/queries/ReportFlavorSharesQuery';
import { uniq } from 'lodash';

import { ReportRequestAction } from '../model/reducer';
import { inheritedFromParams } from '../model/templates';
import { ParentReport } from '../model/types';

interface JobParamsData {
  reportJobByReportId: { params: unknown } | null;
}

export const parseParams = (raw: unknown): Record<string, unknown> | null => {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
};

/**
 * Product names in report params differ from the product table's in case or
 * spacing for ~9% of products, so shares are looked up by this key.
 */
export const productKey = (name: string) => name.trim().toLowerCase();

/**
 * Decomp share (0–1) per reference flavor, per `productKey`, on the original (unversioned) report.
 * A flavor has one row per GGVar; its share is its largest row, the value the optimizer
 * locks to and judges tune ranges and the new-flavor cutoff against.
 */
export const sharesByProduct = (
  nodes: ReportFlavorSharesResponse['allRpFlavorDecomps']['nodes'],
): Map<string, Map<string, number>> => {
  const byProduct = new Map<string, Map<string, number>>();
  nodes.forEach((n) => {
    const share = Number(n.decomp);
    if (
      n.version ||
      !n.product?.name ||
      !n.referenceFlavor ||
      !Number.isFinite(share)
    )
      return;
    const key = productKey(n.product.name);
    const shares = byProduct.get(key) ?? new Map<string, number>();
    shares.set(
      n.referenceFlavor,
      Math.max(share, shares.get(n.referenceFlavor) ?? -Infinity),
    );
    byProduct.set(key, shares);
  });
  return byProduct;
};

/**
 * Loads what an optimization needs from its parent: the demographics and
 * competitive set to inherit, the reference flavors that can be constrained,
 * and each product's current share of those flavors.
 */
const useParentDetails = (
  parent: ParentReport | null,
  dispatch: Dispatch<ReportRequestAction>,
) => {
  const reportId = parent?.reportId;
  const params = useQuery<JobParamsData>(JobParamsQuery, {
    skip: !reportId,
    variables: { reportId },
  });
  const flavors = useQuery<RefFlavorsByReportIdResponse>(RefFlavorsByReportId, {
    skip: !reportId,
    variables: { reportId },
  });
  const shares = useQuery<ReportFlavorSharesResponse>(ReportFlavorSharesQuery, {
    skip: !reportId,
    variables: { reportId },
  });

  const parsed = useMemo(
    () => parseParams(params.data?.reportJobByReportId?.params),
    [params.data],
  );

  useEffect(() => {
    // Apollo may briefly return the previous parent's data; only accept a match.
    if (!reportId || !parsed || params.loading) return;
    dispatch({ type: 'setInherited', inherited: inheritedFromParams(parsed) });
  }, [reportId, parsed, params.loading, dispatch]);

  const refFlavors = useMemo(
    () => uniq((flavors.data?.allRpOfps.nodes ?? []).map((n) => n.attribute)),
    [flavors.data],
  );

  const flavorShares = useMemo(
    () => sharesByProduct(shares.data?.allRpFlavorDecomps.nodes ?? []),
    [shares.data],
  );

  return {
    loading: params.loading,
    error: params.error,
    refFlavors,
    refFlavorsLoading: flavors.loading,
    flavorShares,
    flavorSharesLoading: shares.loading,
    flavorSharesError: shares.error,
    missing: !!reportId && !params.loading && !params.error && !parsed,
  };
};

export default useParentDetails;
